/**
 * HTTP-клиент для FastAPI бэкенда.
 *
 * Особенности:
 * - JWT access-токен передаётся через Authorization: Bearer <token>
 * - Refresh-токен живёт в httpOnly cookie, выставляется бэком
 * - При 401 — автоматический вызов POST /api/auth/refresh
 *   с подменой нового access_token и retry оригинального запроса
 * - Все запросы под /api/* (Vite proxy → backend в dev,
 *   Caddy reverse proxy в prod)
 *
 * Контракт — см. docs/openapi.yaml.
 */

import type { components } from './schema';

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string | undefined;
  public readonly detail: string;

  constructor(status: number, detail: string, code?: string) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.code = code;
  }
}

// Token holder — access-токен живёт только в памяти (XSS-safe per ADR §4).
// Refresh — в httpOnly cookie, не доступен JS вообще.
type TokenListener = (token: string | null) => void;

class TokenStore {
  private accessToken: string | null = null;
  private listeners = new Set<TokenListener>();

  get(): string | null {
    return this.accessToken;
  }

  set(token: string | null): void {
    this.accessToken = token;
    this.listeners.forEach((fn) => {
      fn(token);
    });
  }

  subscribe(fn: TokenListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
}

export const tokenStore = new TokenStore();

// Fetch wrapper

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  /** JSON-сериализуемое тело запроса. */
  json?: unknown;
  /** Не подставлять Authorization (для логина). */
  skipAuth?: boolean;
  /** Не пытаться сделать refresh при 401. */
  skipRefresh?: boolean;
}

const API_BASE = '';

async function refreshAccessToken(): Promise<string> {
  const res = await fetch(`${API_BASE}/api/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    throw new ApiError(res.status, 'Refresh failed', 'refresh_failed');
  }
  const data = (await res.json()) as components['schemas']['AccessTokenResponse'];
  tokenStore.set(data.access_token);
  return data.access_token;
}

let refreshPromise: Promise<string> | null = null;

function getRefreshPromise(): Promise<string> {
  refreshPromise ??= refreshAccessToken().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

export async function apiFetch<TResponse = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<TResponse> {
  const { json, skipAuth, skipRefresh, headers, ...rest } = options;

  const finalHeaders = new Headers(headers);
  finalHeaders.set('Accept', 'application/json');

  let body: BodyInit | undefined;
  if (json !== undefined) {
    finalHeaders.set('Content-Type', 'application/json');
    body = JSON.stringify(json);
  }

  if (!skipAuth) {
    const token = tokenStore.get();
    if (token) {
      finalHeaders.set('Authorization', `Bearer ${token}`);
    }
  }

  const doFetch = (): Promise<Response> =>
    fetch(`${API_BASE}${path}`, {
      ...rest,
      headers: finalHeaders,
      credentials: 'include',
      body,
    });

  let response = await doFetch();

  // Retry once on 401 with refresh
  if (response.status === 401 && !skipAuth && !skipRefresh) {
    try {
      const newToken = await getRefreshPromise();
      finalHeaders.set('Authorization', `Bearer ${newToken}`);
      response = await doFetch();
    } catch {
      tokenStore.set(null);
      throw new ApiError(401, 'Сессия истекла. Войдите снова.', 'session_expired');
    }
  }

  if (response.status === 204) {
    return undefined as TResponse;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const payload: unknown = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    let code: string | undefined;
    if (isJson && payload && typeof payload === 'object') {
      const errPayload = payload as { detail?: unknown; type?: unknown };
      if (typeof errPayload.detail === 'string') detail = errPayload.detail;
      if (typeof errPayload.type === 'string') code = errPayload.type;
    }
    throw new ApiError(response.status, detail, code);
  }

  return payload as TResponse;
}

// Typed shortcuts для auth (всё что нужно в Phase 1)

export const authApi = {
  login: (body: components['schemas']['LoginRequest']) =>
    apiFetch<components['schemas']['LoginResponse']>('/api/auth/login', {
      method: 'POST',
      json: body,
      skipAuth: true,
      skipRefresh: true,
    }),

  refresh: () =>
    apiFetch<components['schemas']['AccessTokenResponse']>('/api/auth/refresh', {
      method: 'POST',
      skipAuth: true,
      skipRefresh: true,
    }),

  logout: () =>
    apiFetch<void>('/api/auth/logout', {
      method: 'POST',
      skipRefresh: true,
    }),

  me: () => apiFetch<components['schemas']['User']>('/api/me'),
};
